/**
 * Module registry for the HERO interactive app-window.
 *
 * Each `HeroModuleKey` maps to a `ModuleEntry` describing the component that
 * renders inside the product window and whether it is `chromeless` (full-bleed,
 * no app top-bar — used by the Mobil and Meeting screens).
 *
 * Every entry points at the real module implementation (`ModuleInbox`,
 * `ModuleDokumente`, …); each renders the window interior (`SIDEBAR_WIDTH`
 * sidebar + flex content at `MODULE_HEIGHT`).
 */
import type { ReactNode } from "react";

import type { HeroModuleKey } from "../hero-types";
import { ModuleAgents } from "./modules/module-agents";
import {
  AnrufbeantworterOverlay,
  ModuleAnrufbeantworter,
} from "./modules/module-anrufbeantworter";
import { ModuleAufgaben } from "./modules/module-aufgaben";
import { ModuleBuchhaltung } from "./modules/module-buchhaltung";
import { ModuleDokumente } from "./modules/module-dokumente";
import { ModuleEtv } from "./modules/module-etv";
import { ModuleFormulare } from "./modules/module-formulare";
import { ModuleFormularePhone } from "./modules/module-formulare-phone";
import { ModuleInbox } from "./modules/module-inbox";
import { InboxChannelsCallout } from "./modules/module-inbox-channels-callout";
import { ModuleKalender } from "./modules/module-kalender";
import { ModuleKontakte } from "./modules/module-kontakte";
import { ModuleKopilot } from "./modules/module-kopilot";
import { ModuleLiegenschaften } from "./modules/module-liegenschaften";
import { MeetingPhoneOverlay, ModuleMeeting } from "./modules/module-meeting";
import { MobilStoreOverlay, ModuleMobil } from "./modules/module-mobil";
import { ModulePortal } from "./modules/module-portal";
import { PortalPhoneOverlay } from "./modules/module-portal-phone";
import { DokumenteTrustSeals } from "./modules/trust-seals";
import { KalenderSourcesOverlay } from "./modules/module-kalender-sources";
import {
  ModuleSerienbrief,
  SerienbriefChannelsOverlay,
} from "./modules/module-serienbrief";
import { ModuleTeamchat } from "./modules/module-teamchat";
import { ModuleTelefon } from "./modules/module-telefon";
import { ModuleTerminbuchung } from "./modules/module-terminbuchung";
import { ModuleWissen } from "./modules/module-wissen";

/** Props every module component receives from the app-window shell. */
export type ModuleProps = {
  readonly active: HeroModuleKey;
  readonly setActive: (key: HeroModuleKey) => void;
  readonly inCall: boolean;
  readonly setInCall: (value: boolean) => void;
};

/** A single registry entry. */
export type ModuleEntry = {
  readonly Component: (props: ModuleProps) => ReactNode;
  /** When true the module renders full-bleed without the app top-bar. */
  readonly chromeless?: boolean;
  /**
   * When true the module keeps the app top-bar but paints its OWN body (its own
   * height + background) instead of the standard fixed-height `#fff` body —
   * used by Kopilot (warm `#EFEEEA` ground, 640px tall).
   */
  readonly customBody?: boolean;
  /**
   * Optional floating overlay rendered at the frame level (a sibling of the
   * window, NOT inside it). Positions itself `position:absolute` relative to
   * the aurora frame so it can overflow/float over the window edges.
   */
  readonly Overlay?: () => ReactNode;
  /**
   * Mobile {@link ResponsiveScale} only: in-flow space below the window for
   * overlays that hang past the bottom edge (`bottom: -42` / `-44` phone mocks).
   * Omit for callouts that sit inside the window (`bottom: 16` / `18`).
   */
  readonly mobileOverlayBleedPx?: number;
};

/** Exhaustive registry — one entry per `HeroModuleKey`. */
export const MODULE_REGISTRY: Record<HeroModuleKey, ModuleEntry> = {
  inbox: {
    Component: ModuleInbox,
    Overlay: InboxChannelsCallout,
    mobileOverlayBleedPx: 48,
  },
  dokumente: {
    Component: ModuleDokumente,
    Overlay: DokumenteTrustSeals,
    mobileOverlayBleedPx: 48,
  },
  posteingangsscan: { Component: ModuleDokumente },
  telefon: { Component: ModuleTelefon },
  portal: {
    Component: ModulePortal,
    Overlay: PortalPhoneOverlay,
    mobileOverlayBleedPx: 48,
  },
  serienbrief: {
    Component: ModuleSerienbrief,
    Overlay: SerienbriefChannelsOverlay,
    mobileOverlayBleedPx: 48,
  },
  teamchat: { Component: ModuleTeamchat },
  kontakte: { Component: ModuleKontakte },
  liegenschaften: { Component: ModuleLiegenschaften },
  aufgaben: { Component: ModuleAufgaben },
  terminbuchung: { Component: ModuleTerminbuchung },
  anrufbeantworter: {
    Component: ModuleAnrufbeantworter,
    Overlay: AnrufbeantworterOverlay,
  },
  formulare: {
    Component: ModuleFormulare,
    Overlay: ModuleFormularePhone,
    mobileOverlayBleedPx: 48,
  },
  meeting: {
    Component: ModuleMeeting,
    chromeless: true,
    Overlay: MeetingPhoneOverlay,
    mobileOverlayBleedPx: 48,
  },
  wissen: { Component: ModuleWissen },
  agents: { Component: ModuleAgents },
  kopilot: { Component: ModuleKopilot, customBody: true },
  kalender: {
    Component: ModuleKalender,
    Overlay: KalenderSourcesOverlay,
    mobileOverlayBleedPx: 48,
  },
  etv: { Component: ModuleEtv },
  mobil: {
    Component: ModuleMobil,
    chromeless: true,
    Overlay: MobilStoreOverlay,
    mobileOverlayBleedPx: 24,
  },
  buchhaltung: { Component: ModuleBuchhaltung },
};
